import { useQueries } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { ApiError } from "@/lib/api-error";
import { endpoints, type DocumentEnvelope, type InvoiceDetail } from "@/lib/endpoints";
import { queryKeys } from "@/lib/query-keys";
import { isModuleOff, useNotifications } from "@/features/notifications/use-notifications";

/**
 * "WHICH BILLS ARE STILL WAITING FOR AN E-WAY BILL NUMBER?" (Rule 138, §17.7)
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THE PERSON WHO GENERATES THE E-WAY BILL IS THE ONE WHO CANNOT SEE THE LIST.
 *
 * The owner and his CA settled it in writing (see `EwayBillCard.tsx`): this app
 * cannot generate an e-way bill and never will — she logs into the government
 * portal, and the twelve digits are typed in here afterwards. `eway.record` is
 * therefore HER write, and the flagship notification is addressed to whoever
 * holds it.
 *
 * But a notification is a nudge, not a work queue. It tells her about ONE bill,
 * once, and it never comes back to say the job is still open. Answering "what is
 * still outstanding" meant opening the hub, reading the e-way lines out of every
 * other kind of alert, opening each bill in turn and looking at the card — for a
 * question she is asked every time a lorry is loaded. That is the hunting this
 * exists to remove.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TWO REQUESTS, AND WHY IT IS NOT ONE.
 *
 * 1. THE CANDIDATES COME FROM THE NOTIFICATION FEED, filtered server-side to
 *    `invoice.eway_bill.required` (`GET /notifications?event=`, which the API
 *    has always accepted). That event is raised by `ewayBillRequirement` — the
 *    SAME function in `@steelbill/types` that draws the card on the bill (§2.1)
 *    — so "over the threshold" is decided in exactly one place and this screen
 *    does not re-derive it from figures of its own.
 *
 *    THE INVOICE REGISTER CANNOT ANSWER THIS. `GET /invoices` has no e-way
 *    filter, and its rows carry no `ewbNo` at all; the requirement also depends
 *    on the LINES (a pure-service bill of any size needs none — CBIC FAQ), which
 *    the register deliberately does not send. A client-side filter over the
 *    register would therefore have to guess, on a compliance question where the
 *    penalty is 200% of the tax and the lorry.
 *
 * 2. THE FLAG IS NOT THE ANSWER, SO EACH CANDIDATE IS VERIFIED. Nothing
 *    withdraws an `invoice.eway_bill.required` row when the number is finally
 *    recorded — it is raised once at the create and left as evidence that
 *    somebody was told. Listing the feed alone would produce a queue that only
 *    ever grows, which is the same as no queue. So each candidate's own invoice
 *    is read and kept only while `ewbNo` is empty.
 *
 *    THAT READ IS `queryKeys.invoices.detail(id)` — THE SAME CACHE ENTRY THE
 *    BILL'S OWN SCREEN USES, deliberately. Tapping a row is then instant rather
 *    than a second fetch of what was just fetched, and when she saves the number
 *    `EwayBillCard` invalidates `queryKeys.invoices.all`, so the row leaves this
 *    list on its own with nothing here subscribing to anything.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * WHAT THIS CANNOT SEE, stated rather than glossed: a notification is addressed
 * to the people who held `eway.record` ON THE DAY THE BILL WAS SAVED. Bills
 * flagged before her account existed are in the owner's inbox and not in hers,
 * so her queue begins on the day she gets a login. The alternative — a
 * server-side "every invoice over the threshold with no EBN" filter — is an API
 * change, and the register cannot express it today.
 */

/** How many flagged rows to ask for. Rule 138 fires a few times a WEEK here. */
const CANDIDATE_LIMIT = 50;

/**
 * How many of them get their invoice read.
 *
 * ONE REQUEST PER ROW IS THE COST OF VERIFYING, and on a workshop mobile
 * connection an unbounded fan-out is the kind of thing that turns a helpful
 * panel into the reason the app feels slow. Twenty is far beyond any real
 * backlog — the alert fires a handful of times a week — and the panel says so
 * out loud when it has had to stop short, rather than quietly under-reporting a
 * compliance queue.
 */
const MAX_VERIFIED = 20;

/** One bill still waiting for its twelve digits. */
export interface EwayPendingRow {
  invoiceId: number;
  /** `26-27/67` (§6). */
  number: string;
  /** `YYYY-MM-DD` — a calendar day. Format with `useFormatDay`, never `new Date`. */
  date: string;
  customerName: string | null;
  /** INTEGER PAISE, from the notification's payload. Format through `formatRupees`. */
  consignmentPaise: number | null;
  thresholdPaise: number | null;
  /** An ISO INSTANT — when the alert was raised. */
  flaggedAt: string;
}

export interface EwayPending {
  rows: EwayPendingRow[];
  /** Still finding out. The panel shows nothing rather than a flash of "none". */
  loading: boolean;
  /**
   * Nobody asked, or the `notifications` module is switched off (§18.3 — the
   * 404 IS the flag). The panel renders NOTHING in this case: a reports screen
   * announcing that an unrelated module is off is noise, and the compliance
   * alert it would be explaining does not exist on that build either.
   */
  unavailable: boolean;
  /** Flagged bills left unverified — over {@link MAX_VERIFIED}, or a failed read. */
  unverified: number;
}

/** A payload field that must be an integer number of paise, or nothing. */
function paise(payload: Record<string, unknown>, key: string): number | null {
  const value = payload[key];
  return typeof value === "number" && Number.isInteger(value) ? value : null;
}

/**
 * @param enabled — pass `can("eway.record")`. False makes NO request: the server
 * would refuse it and `requireCap` writes every denial to `audit_log` (§2.1).
 */
export function useEwayPending(enabled: boolean): EwayPending {
  const flagged = useNotifications({
    event: "invoice.eway_bill.required",
    limit: CANDIDATE_LIMIT,
    enabled,
  });

  /*
   * DEDUPED BY INVOICE, and the entity type is checked rather than assumed. The
   * server's unique index already makes a second row for the same bill
   * impossible, so this is belt-and-braces — but a queue that listed one bill
   * twice would have somebody generating a second e-way bill for a consignment
   * that already had one, which is a worse mistake than the one it prevents.
   */
  const seen = new Set<number>();
  const candidates = (flagged.data?.notifications ?? [])
    .filter((row) => row.entityType === "invoice")
    .filter((row) => {
      if (seen.has(row.entityId)) return false;
      seen.add(row.entityId);
      return true;
    });

  /*
   * OLDEST FIRST — the opposite of the hub's order, on purpose.
   *
   * The hub is an inbox and answers "what have I not seen"; this is a queue and
   * answers "what has been waiting longest". A consignment flagged on Tuesday
   * and still without a number is closer to moving on the wrong paper than one
   * flagged an hour ago, so it belongs at the top even though the newest row is
   * the one the bell just rang for.
   */
  const oldestFirst = [...candidates].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  const verifying = oldestFirst.slice(0, MAX_VERIFIED);

  const details = useQueries({
    queries: verifying.map((row) => ({
      // THE BILL SCREEN'S OWN KEY. See the header — shared cache, and a recorded
      // number clears this list without a subscription of any kind.
      queryKey: queryKeys.invoices.detail(row.entityId),
      queryFn: async (): Promise<InvoiceDetail> => {
        const response = await api.get<DocumentEnvelope<InvoiceDetail>>(
          endpoints.invoice(row.entityId),
        );
        return response.data;
      },
      staleTime: 5 * 60_000,
    })),
  });

  const moduleOff = flagged.isError && isModuleOff(flagged.error);
  if (!enabled || moduleOff) {
    return { rows: [], loading: false, unavailable: true, unverified: 0 };
  }

  const rows: EwayPendingRow[] = [];
  let unverified = Math.max(0, oldestFirst.length - verifying.length);

  verifying.forEach((row, index) => {
    const detail = details[index];
    if (detail === undefined || detail.isPending) return;

    if (detail.isError) {
      /*
       * A 404 IS NOT A FAILURE TO REPORT. The `invoices` flag being off, or a
       * bill that no longer exists, means there is nothing to record a number
       * against — counting it as "could not check" would leave a permanent
       * warning nobody can act on. Anything else genuinely was not checked.
       */
      const gone = detail.error instanceof ApiError && detail.error.status === 404;
      if (!gone) unverified += 1;
      return;
    }

    const invoice = detail.data;
    if (invoice === undefined) return;

    // A CANCELLED bill is withdrawn — whatever was going to move is not, and
    // the API refuses to record a number against one (409). The same guard the
    // server applies before raising the alert at all.
    if (invoice.status === "CANCELLED") return;

    // RECORDED, so it is done. Empty string as well as null: the API stores the
    // column nullable and `nonEmpty()` normalises, but a blank is not a number.
    if (invoice.ewbNo !== null && invoice.ewbNo !== "") return;

    const payload =
      typeof row.payload === "object" && row.payload !== null
        ? (row.payload as Record<string, unknown>)
        : {};

    rows.push({
      invoiceId: invoice.id,
      /*
       * THE INVOICE'S OWN NUMBER AND DATE, NOT THE PAYLOAD'S. The payload was
       * written when the bill was saved and is a snapshot; the document is the
       * record. They agree today — an issued invoice's number never changes
       * (§4) — and when they cannot, the thing she is about to open is the one
       * worth naming.
       */
      number: invoice.number,
      date: invoice.date,
      customerName: invoice.customer?.name ?? null,
      // These two exist ONLY in the payload: the requirement was computed
      // server-side from the lines, and nothing on the document stores it.
      consignmentPaise: paise(payload, "consignmentPaise"),
      thresholdPaise: paise(payload, "thresholdPaise"),
      flaggedAt: row.createdAt,
    });
  });

  return {
    rows,
    /*
     * LOADING UNTIL EVERY VERIFICATION HAS ANSWERED. Showing a partial queue
     * would let the panel say "one bill is waiting" and then say "three" a
     * second later, on a compliance figure — and an empty state that appears
     * for a moment before three rows arrive reads as "nothing to do".
     */
    loading: flagged.isPending || details.some((detail) => detail.isPending),
    unavailable: false,
    unverified,
  };
}
